import React from "react";
import styled from "styled-components";

import { Flex } from "../../styles/global-styles";
import {
  breakpoints,
  colors,
  fontFamily,
  getProjectTheme,
  sectionRhythm,
} from "../../../theme";
import { PROJECT_JIFFY } from "../../utils/constants/projects";
import { projectImage } from "../../utils/cdn";
import ContactMeSection from "../contact-me-section";
import Testimonials from "../reviews-section/Testimonials";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";

const projectTheme = getProjectTheme("jiffy");

const PageContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  img {
    width: 100%;
    height: auto;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 32px;
  }
`;

const HelperText = styled.span`
  color: ${colors.grey.darkMedium};
`;

const MetaRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 18px;
  background: ${colors.white};
  margin: 1.5rem 0 0.5rem;
`;

const ImageCaption = styled.figcaption`
  display: block;
  font-size: 14px;
  text-align: center;
  color: ${colors.grey.darkMedium};
  margin-bottom: 1.5rem;
`;

const OverviewSection = styled.div`
  margin-top: 2rem;
  margin-bottom: 0;
  display: grid;
  gap: 1.5rem;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
`;

const OverviewItem = styled.div`
  flex: 1;
  min-width: 140px;
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;

  a {
    color: inherit;
    word-break: break-all;

    &:hover {
      color: ${colors.linkHover.light};
      text-decoration: underline;
    }
  }
`;

const Body = styled.div`
  ${caseStudyBodyStyles}
`;

const JiffyProjectSection = () => {
  const {
    client,
    services,
    industry,
    techStack,
    links,
    year,
    heading,
    media,
    roles,
  } = PROJECT_JIFFY;
  const yearLabel = year.length > 1 ? `${year[0]}-${year[1]}` : year[0];

  return (
    <>
      <PageContainer>
        <Title>{heading}</Title>
        <MetaRow>
          <HelperText>Role: {roles.join(", ")}</HelperText>
          <HelperText>Year: {yearLabel}</HelperText>
        </MetaRow>

        <div style={{ marginTop: "1.5rem" }}>
          <ProjectHero
            src={media.cover.desktop}
            alt="Jiffy Shirts e-commerce storefront"
          />
        </div>

        <OverviewSection>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{client.name}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{client.country}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{services.join(", ")}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{industry}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewItem>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {links?.project ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={links.project}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.project}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <Body>
          <section>
            <h4>The product</h4>
            <p>
              Jiffy Shirts is a U.S. based e-commerce company for blank apparel
              and custom printing. They sell DTF transfers, stickers, and
              on-demand printed goods to creators, small businesses, and
              enterprise clients.
            </p>
            <p>
              Fast fulfillment and clear pricing are a big part of the product.
              Customers browse the catalog, customize items, and check out with
              upfront costs and delivery timelines.
            </p>
            <p>
              I worked on the Rails+Vue frontend across catalogs,
              customizations, cart, checkout, accounts, and print workflows.
            </p>
            <figure>
              <ContentImage
                src={projectImage("jiffy-dtf-design")}
                alt="Jiffy DTF design tool"
              />
              <ImageCaption>
                The DTF design flow where customers upload art and preview
                transfers.
              </ImageCaption>
            </figure>
          </section>

          <section>
            <h4>Live store work</h4>
            <p>
              My responsibilities varied from fixing bugs on the live store,
              where broken UI or wrong prices could block purchases, to building
              new features for the product catalog and checkout flows.
            </p>
            <p>
              I traced pricing mismatches across product and checkout flows
              where the price on screen did not always match what customers were
              charged. After finding the bad state handling, I shipped a fix so
              pricing matched from the product page through checkout.
            </p>
            <p>
              I also fixed cross-browser rendering and responsive layout bugs by
              reproducing issues in specific browsers, tightening layout rules,
              and checking fixes across viewports.
            </p>
            <p>
              I built the cookie consent banner for production so it met
              compliance rules without getting in the way of browsing or
              checkout.
            </p>
            <figure>
              <ContentImage
                src={projectImage("jiffy-cart")}
                alt="Jiffy cart page"
              />
              <ImageCaption>
                Cart and checkout where pricing and totals had to stay in sync.
              </ImageCaption>
            </figure>
          </section>

          <section>
            <h4>Outcome</h4>
            <p>
              I cut down layout shift on product pages by fixing how content
              loaded in. Pages stopped jumping around as images and prices
              appeared.
            </p>
            <p>
              I removed old A/B experiment code from the product detail page.
              That cleaned up the PDP and made later changes easier.
            </p>
            <p>
              I added analytics and event tracking so product and marketing
              could track key flows. I also updated the company blog theme,
              built a responsive footer, and fixed mobile layout issues on
              several templates.
            </p>
            <p>
              The stack is a Rails app on the backend with JavaScript,
              TypeScript, Vue, SCSS, and Tailwind on the frontend. I added
              stable data-testid attributes for automation and worked with CI/CD
              review environments and QA before shipping to production.
            </p>
            <p>
              The frontend is in better shape for day-to-day work and holds up
              better on mobile.
            </p>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials showMore={false} reviews={client.reviews} />
          </section>
        </Body>
      </PageContainer>
      <ContactMeSection />
    </>
  );
};

export default JiffyProjectSection;
